import React, { useCallback } from "react";
import { Box, Button, Typography, IconButton } from "@mui/material";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { MmkItem } from "../../types/mmk";
import { postMmkItemAction } from "../../axios/mmk";
import { getFocusSessionProgress } from "../../hooks/useMmkFocusSession";
import { useHorizontalSwipe } from "../../hooks/useHorizontalSwipe";
import { MmkCard } from "./mmk-card";
import {
  mmkBackToMmkButtonStyles,
  mmkFocusSessionCounterStyles,
} from "./styles";
import { MmkPageShell } from "./mmk-page-shell";

interface FocusSessionViewProps {
  currentItem: MmkItem;
  sessionItems: MmkItem[];
  sessionTotal: number;
  focusSessionIndex: number;
  flipped: boolean;
  onFlip: (flip: boolean) => void;
  onPrev: () => void;
  onNext: () => void;
  onMarkAsKnown: (id: string) => void;
  onExit: () => void;
  onPin: (id: string) => void;
  onReviewLater: (id: string) => void;
}

export const FocusSessionView: React.FC<FocusSessionViewProps> = ({
  currentItem,
  sessionItems,
  sessionTotal,
  focusSessionIndex,
  flipped,
  onFlip,
  onPrev,
  onNext,
  onMarkAsKnown,
  onExit,
  onPin,
  onReviewLater,
}) => {
  const { current: progressCurrent, total: progressTotal } =
    getFocusSessionProgress(
      focusSessionIndex,
      sessionItems.length,
      sessionTotal,
    );

  const handleCardFlip = () => {
    if (flipped) {
      onFlip(false);
      return;
    }
    onFlip(true);
    postMmkItemAction(currentItem.id, "flip").catch(() => {});
  };

  const canGoPrev = focusSessionIndex > 0;
  const canGoNext = focusSessionIndex < sessionItems.length - 1;

  const handleSwipePrev = useCallback(() => {
    if (canGoPrev) onPrev();
  }, [canGoPrev, onPrev]);

  const handleSwipeNext = useCallback(() => {
    if (canGoNext) onNext();
  }, [canGoNext, onNext]);

  const swipeHandlers = useHorizontalSwipe({
    enabled: sessionItems.length > 1,
    onSwipeLeft: handleSwipeNext,
    onSwipeRight: handleSwipePrev,
  });

  return (
    <MmkPageShell>
      <Box
        sx={{
          position: "relative",
          flex: 1,
          minHeight: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          boxSizing: "border-box",
          // Keep card clear of the absolute Back button.
          pt: { xs: 6, sm: 7 },
          pb: 2,
        }}
      >
        <Button
          variant="outlined"
          onClick={onExit}
          sx={{
            ...mmkBackToMmkButtonStyles,
            position: "absolute",
            top: 0,
            right: 0,
          }}
          startIcon={<X size={18} />}
        >
          Back to MMM
        </Button>

        <Box {...swipeHandlers} sx={{ width: "100%", touchAction: "pan-y" }}>
          <MmkCard
            key={currentItem.id}
            item={currentItem}
            isActive={flipped}
            isFlipped={flipped}
            isSelectionMode={false}
            isSelected={false}
            forceDefaultVariant
            fullLength
            onToggleSelect={() => {}}
            onFlip={handleCardFlip}
            onClose={() => onFlip(false)}
            onPin={() => onPin(currentItem.id)}
            onReviewLater={() => onReviewLater(currentItem.id)}
            onKnowIt={() => onMarkAsKnown(currentItem.id)}
          />
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              mt: 2,
            }}
          >
            <IconButton
              onClick={onPrev}
              disabled={!canGoPrev}
              sx={{ border: "1px solid", borderColor: "divider" }}
            >
              <ChevronLeft size={24} />
            </IconButton>
            <Typography sx={mmkFocusSessionCounterStyles}>
              {progressCurrent} / {progressTotal}
            </Typography>
            <IconButton
              onClick={onNext}
              disabled={!canGoNext}
              sx={{ border: "1px solid", borderColor: "divider" }}
            >
              <ChevronRight size={24} />
            </IconButton>
          </Box>
        </Box>
      </Box>
    </MmkPageShell>
  );
};
